import { FeatureGroup, useMap } from "react-leaflet";
import {
  leafletLayer,
  type PaintRule,
  PolygonSymbolizer,
} from "protomaps-leaflet";
import { useCallback, useEffect, useRef, useState } from "react";
import { type GridLayer, type LayersControlEvent } from "leaflet";
import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import { useProtomapsPrintAdapter } from "@/features/map/hooks/useProtomapsPrintAdapter";
import type { PrintRenderContext } from "@/features/map/utils/printRenderScale";

/**
 * 斜線のCanvasを生成する
 *
 * @param {string} [color='#000000'] - 斜線の色
 * @param {number} [size=10] - canvasエレメントの縦横のサイズ(pixel)。10なら10x10サイズとなる
 * @param {number} [thickness=1] - 斜線の線幅(pixel)
 * @returns {OffscreenCanvas} canvasエレメント
 */
export const createHatchCanvas = (
  color: string,
  size: number,
  thickness: number
): OffscreenCanvas => {
  // OffscreenCanvasを使うことで通常のcanvasより性能が良くなる
  const canvas = new OffscreenCanvas(size, size);
  const ctx = canvas.getContext("2d");

  if (ctx) {
    // 斜線の描画
    ctx.strokeStyle = color;
    ctx.lineWidth = thickness;
    ctx.beginPath();
    ctx.moveTo(0, size);
    ctx.lineTo(size, 0);
    // 境界部分で線が途切れないよう、左上、右下の角にも線を引く
    // 左上角の描画
    ctx.moveTo(-size / 4, size / 4);
    ctx.lineTo(size / 4, -size / 4);
    // 右下角の描画
    ctx.moveTo(size * 0.75, size * 1.25);
    ctx.lineTo(size * 1.25, size * 0.75);
    ctx.stroke();
  }

  return canvas;
};

interface Props {
  defaultVisible: boolean;
  visible?: boolean;
}

// UIに表示するレイヤー名
export const FIRE_PROTECTION_AREA_LAYER_NAME = "防火・準防火地域";

// pmtilesが内部で保持しているレイヤー名
export const FIRE_PROTECTION_DATA_LAYER_NAME = "bouka";
export const QUASI_FIRE_PROTECTION_DATA_LAYER_NAME = "junbouka";

// ズームレベル
// pmtiles生成時に使ったズームレベルの値と同じでなくても問題ない
// ポリゴンの表示ズームレベルの設定、ポリゴンはハザードレイヤーと同一ズームレベルで表示する
export const FIRE_PROTECTION_POLYGON_MIN_ZOOM = 10;
export const FIRE_PROTECTION_POLYGON_MAX_ZOOM = 22;

const QUASI_FIRE_PROTECTION_POLYGON_MIN_ZOOM = FIRE_PROTECTION_POLYGON_MIN_ZOOM;
const QUASI_FIRE_PROTECTION_POLYGON_MAX_ZOOM = FIRE_PROTECTION_POLYGON_MAX_ZOOM;

// ポリゴンの線のデフォルトスタイルを設定
const DEFAULT_FIRE_PROTECTION_POLYGON_OPACITY = 0.25;
const DEFAULT_FIRE_PROTECTION_POLYGON_STROKE_COLOR = "#EA3278";
const DEFAULT_FIRE_PROTECTION_POLYGON_HATCH_COLOR = "#EA3278";
const DEFAULT_FIRE_PROTECTION_POLYGON_WIDTH = 4;

const DEFAULT_QUASI_FIRE_PROTECTION_POLYGON_OPACITY = 0.25;
const DEFAULT_QUASI_FIRE_PROTECTION_POLYGON_STROKE_COLOR = "#E1AD01";
const DEFAULT_QUASI_FIRE_PROTECTION_POLYGON_HATCH_COLOR = "#E1AD01";
const DEFAULT_QUASI_FIRE_PROTECTION_POLYGON_WIDTH = 4;

// 斜線の設定値
const HATCH_LINE_WIDTH = 4; // 斜線の線幅
const HATCH_PATTERN_SIZE = 16; // 斜線の線と線の間隔に影響する

const host = process.env.VITE_COMMON_TILE_HOST ?? "";
const url = `${host}/bouka-tiles/bouka_junbouka_z18.pmtiles`;

// 右下に表示する項目
const attribution =
  '<a href="https://nlftp.mlit.go.jp/ksj/other/agreement.html">国土数値情報</a>';

export const buildFireProtectionAreaPaintRules = (
  renderScale: number,
  zoomOffset: number
): PaintRule[] => [
  {
    dataLayer: QUASI_FIRE_PROTECTION_DATA_LAYER_NAME,
    symbolizer: new PolygonSymbolizer({
      pattern: createHatchCanvas(
        DEFAULT_QUASI_FIRE_PROTECTION_POLYGON_HATCH_COLOR,
        HATCH_PATTERN_SIZE * renderScale,
        HATCH_LINE_WIDTH * renderScale
      ),
      fill: "transparent",
      opacity: DEFAULT_QUASI_FIRE_PROTECTION_POLYGON_OPACITY,
      stroke: DEFAULT_QUASI_FIRE_PROTECTION_POLYGON_STROKE_COLOR,
      width: DEFAULT_QUASI_FIRE_PROTECTION_POLYGON_WIDTH * renderScale,
    }),
    minzoom: QUASI_FIRE_PROTECTION_POLYGON_MIN_ZOOM + zoomOffset,
    maxzoom: QUASI_FIRE_PROTECTION_POLYGON_MAX_ZOOM + zoomOffset,
  },
  {
    dataLayer: FIRE_PROTECTION_DATA_LAYER_NAME,
    symbolizer: new PolygonSymbolizer({
      pattern: createHatchCanvas(
        DEFAULT_FIRE_PROTECTION_POLYGON_HATCH_COLOR,
        HATCH_PATTERN_SIZE * renderScale,
        HATCH_LINE_WIDTH * renderScale
      ),
      fill: "transparent",
      opacity: DEFAULT_FIRE_PROTECTION_POLYGON_OPACITY,
      stroke: DEFAULT_FIRE_PROTECTION_POLYGON_STROKE_COLOR,
      width: DEFAULT_FIRE_PROTECTION_POLYGON_WIDTH * renderScale,
    }),
    minzoom: FIRE_PROTECTION_POLYGON_MIN_ZOOM + zoomOffset,
    maxzoom: FIRE_PROTECTION_POLYGON_MAX_ZOOM + zoomOffset,
  },
];

// 防火・準防火地域レイヤー
export const FireProtectionAreaLayer: React.FC<Props> = ({
  defaultVisible,
  visible: visibleProp,
}) => {
  const map = useMap();
  const [visible, setVisible] = useState(defaultVisible);
  const layerRef = useRef<GridLayer | null>(null);
  const createPrintLayer = useCallback(
    (context: PrintRenderContext): GridLayer =>
      leafletLayer({
        url,
        paintRules: buildFireProtectionAreaPaintRules(
          context.printScale,
          context.zoomOffset
        ),
        attribution,
      }) as GridLayer,
    []
  );

  useProtomapsPrintAdapter({
    map,
    enabled: visible,
    normalLayerRef: layerRef,
    createPrintLayer,
  });

  useEffect(() => {
    if (!map) return;

    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
      return;
    }

    const handleOverlayAdd = (e: LayersControlEvent): void => {
      if (e.name === FIRE_PROTECTION_AREA_LAYER_NAME) {
        setVisible(true);
      }
    };

    const handleOverlayRemove = (e: LayersControlEvent): void => {
      if (e.name === FIRE_PROTECTION_AREA_LAYER_NAME) {
        setVisible(false);
      }
    };

    map.on("overlayadd", handleOverlayAdd);
    map.on("overlayremove", handleOverlayRemove);

    return () => {
      map.off("overlayadd", handleOverlayAdd);
      map.off("overlayremove", handleOverlayRemove);
    };
  }, [map, visibleProp]);

  useEffect(() => {
    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
    }
  }, [visibleProp]);

  useEffect(() => {
    if (!map) return;

    const layer = leafletLayer({
      url,
      paintRules: buildFireProtectionAreaPaintRules(1, 0),
      attribution,
    }) as GridLayer;

    layer.setZIndex(LAYER_Z_INDEX.FIRE_PROTECTION_AREA);
    layerRef.current = layer;

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!map || !layerRef.current) return;

    if (visible) {
      layerRef.current.addTo(map);
    } else {
      map.removeLayer(layerRef.current);
    }
  }, [map, visible]);

  return <FeatureGroup />;
};
